import { spiralRotationDeg } from '@stair-craft/calc/util';
import { IStairCalculationResult, IStairConfig } from '@stair-craft/common/util';

import {
    EColorRef,
    EPrimitiveKind,
    ETextAlign,
    ETextBaseline,
    FONT_MONO_FAMILY,
    IFont,
    IPaneRect,
    IPlanPrimitive,
    IProjectionOutput,
    emptyProjection,
} from './plan-primitive';

const PAD_TOP: number = 32;
const PAD_BOTTOM: number = 40;
const PAD_LEFT: number = 24;
const PAD_RIGHT: number = 24;
const MIN_INNER: number = 60;
const SUB_LABEL_GAP: number = 22;
const RADIUS_FACTOR: number = 0.42;
const EXIT_RAMP_DEG: number = 90;
const ARROW_LEN: number = 10;
const ARROW_SPREAD: number = 0.18;

const CENTER_FONT: IFont = { weight: 500, size: 16, family: FONT_MONO_FAMILY };
const SUB_FONT: IFont = { weight: 400, size: 10, family: FONT_MONO_FAMILY };

/**
 * Spiral rotation indicator (угол поворота) — a dial: a faint full circle, the
 * active sweep arc of the treads (the stair's own rotation, clockwise/ccw per turn
 * direction) with an arrow head, the exit platform's 90° after it as a thin dashed arc,
 * the stair's rotation in degrees in the centre, and a step-count/riser sub-label.
 * Pure projection in pane-local pixels.
 */
export function projectSpiralRotation(result: IStairCalculationResult, config: IStairConfig, pane: IPaneRect): IProjectionOutput {
    // The stair turns by its own rotation; the exit platform's quarter turn after the last tread
    // is drawn apart and is not counted in the stair's degrees. With no rotation in the config the
    // calculator picks it from the floor height; the dial shows that pick, rounded to a degree.
    const totalRotationDeg: number = spiralRotationDeg(config);
    const clockwise: boolean = config.turnDirection === 'right';

    const innerW: number = pane.w - PAD_LEFT - PAD_RIGHT;
    const innerH: number = pane.h - PAD_TOP - PAD_BOTTOM;
    if (innerW < MIN_INNER || innerH < MIN_INNER) {
        return emptyProjection();
    }

    const radius: number = Math.min(innerW, innerH - SUB_LABEL_GAP) * RADIUS_FACTOR;
    const cx: number = pane.w / 2;
    const cy: number = pane.h - PAD_BOTTOM - SUB_LABEL_GAP - radius;

    const primitives: IPlanPrimitive[] = [];

    // Faint full circle outline.
    primitives.push({
        kind: EPrimitiveKind.Arc,
        center: [cx, cy],
        startAngle: 0,
        endAngle: Math.PI * 2,
        stroke: EColorRef.Ink4,
        width: 1,
        radius,
    });

    // Active sweep arc.
    const totalRad: number = (totalRotationDeg * Math.PI) / 180;
    const startAng: number = -Math.PI / 2;
    const endAng: number = clockwise ? startAng + totalRad : startAng - totalRad;
    primitives.push({
        kind: EPrimitiveKind.Arc,
        center: [cx, cy],
        startAngle: startAng,
        endAngle: endAng,
        stroke: EColorRef.Ink2,
        width: 2.2,
        anticlockwise: !clockwise,
        radius,
    });

    // The exit platform: a quarter turn past the last tread, thin and dashed.
    const exitRad: number = (EXIT_RAMP_DEG * Math.PI) / 180;
    primitives.push({
        kind: EPrimitiveKind.Arc,
        center: [cx, cy],
        startAngle: endAng,
        endAngle: clockwise ? endAng + exitRad : endAng - exitRad,
        stroke: EColorRef.Ink3,
        width: 1,
        dash: [4, 3],
        anticlockwise: !clockwise,
        radius,
    });

    // Arrow head at the end of the active arc — tangent to the circle.
    const tipX: number = cx + radius * Math.cos(endAng);
    const tipY: number = cy + radius * Math.sin(endAng);
    const tangent: number = endAng + (clockwise ? Math.PI / 2 : -Math.PI / 2);
    const baseX: number = tipX - ARROW_LEN * Math.cos(tangent);
    const baseY: number = tipY - ARROW_LEN * Math.sin(tangent);
    const sideX: number = ARROW_LEN * Math.sin(tangent) * ARROW_SPREAD * 6;
    const sideY: number = -ARROW_LEN * Math.cos(tangent) * ARROW_SPREAD * 6;
    primitives.push({
        kind: EPrimitiveKind.Poly,
        points: [
            [tipX, tipY],
            [baseX + sideX, baseY + sideY],
            [baseX - sideX, baseY - sideY],
        ],
        closed: true,
        fill: EColorRef.Ink2,
    });

    // Centre label — total rotation in degrees.
    primitives.push({
        kind: EPrimitiveKind.Text,
        at: [cx, cy],
        text: `${String(Math.round(totalRotationDeg))}°`,
        align: ETextAlign.Center,
        baseline: ETextBaseline.Middle,
        color: EColorRef.Ink,
        font: CENTER_FONT,
    });

    // Sub-label — step count + riser height.
    const stepsAbbr: string = $localize`:@@planLabelStepsAbbr:ст.`;
    primitives.push({
        kind: EPrimitiveKind.Text,
        at: [cx, cy + radius + 8],
        text: `${String(result.stepCount)} ${stepsAbbr} · h ${String(Math.round(result.riserHeight))}`,
        align: ETextAlign.Center,
        baseline: ETextBaseline.Top,
        color: EColorRef.Ink3,
        font: SUB_FONT,
    });

    // The dial is schematic: a degrees indicator with no millimetre scale at all.
    return { primitives, pxPerMm: null, origin: { x: 0, y: 0 } };
}
